import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarCheckIcon, FlaskConicalIcon, TimerIcon, StarIcon, ArrowRightIcon, FileBarChart2Icon, WrenchIcon, BookmarkIcon, TrendingUpIcon } from 'lucide-react';
import { runReport } from '../utils/reportEngine';
import { getReport, premadeReports } from '../data/reports';
import { ReportChart } from '../components/reports/ReportChart';
import { useApp } from '../context/AppContext';
const kpis = [{
  label: 'Appointments (30d)',
  value: '243',
  delta: '+8% vs prior',
  icon: CalendarCheckIcon
}, {
  label: 'Results Received',
  value: '20',
  delta: '95% negative',
  icon: FlaskConicalIcon
}, {
  label: 'Avg Turnaround',
  value: '24.7 hrs',
  delta: '−3.2 hrs vs prior',
  icon: TimerIcon
}, {
  label: 'Avg Survey Rating',
  value: '4.2 / 5',
  delta: '16 responses',
  icon: StarIcon
}];
export function Overview() {
  const {
    templates
  } = useApp();
  const bookingReport = getReport('appts-by-booking-method');
  const statusReport = getReport('appt-status-breakdown');
  const bookingResult = bookingReport ? runReport(bookingReport.config) : null;
  const statusResult = statusReport ? runReport(statusReport.config) : null;
  return <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8" data-id="element-303">
      <div className="mb-6" data-id="element-304">
        <h2 className="text-xl font-semibold tracking-tight text-navy-900" data-id="element-305">
          Good morning, Robert
        </h2>
        <p className="mt-1 text-sm text-navy-500" data-id="element-306">
          Here's what's happening across your testing program.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-307">
        {kpis.map((kpi, i) => <motion.div key={kpi.label} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.06,
        duration: 0.35,
        ease: 'easeOut'
      }} className="card-float rounded-2xl bg-white p-5" data-id="element-308">
            <div className="flex items-center justify-between" data-id="element-309">
              <p className="text-xs font-medium uppercase tracking-wider text-navy-400" data-id="element-310">
                {kpi.label}
              </p>
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy-50" data-id="element-311">
                <kpi.icon className="h-4 w-4 text-navy-700" data-id="element-312" />
              </span>
            </div>
            <p className="mt-2 text-2xl font-semibold tracking-tight text-navy-900" data-id="element-313">
              {kpi.value}
            </p>
            <p className="mt-1 flex items-center gap-1 text-xs text-navy-400" data-id="element-314">
              <TrendingUpIcon className="h-3 w-3 text-emerald-500" data-id="element-315" />
              {kpi.delta}
            </p>
          </motion.div>)}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2" data-id="element-316">
        <motion.section initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.26,
        duration: 0.35,
        ease: 'easeOut'
      }} className="card-float rounded-2xl bg-white" aria-labelledby="chart-booking" data-id="element-317">
          <div className="flex items-center justify-between border-b border-navy-50 px-5 py-4" data-id="element-318">
            <div data-id="element-319">
              <h3 id="chart-booking" className="text-sm font-semibold text-navy-900" data-id="element-320">
                Appointments by Booking Method
              </h3>
              <p className="text-xs text-navy-400" data-id="element-321">Last 30 days</p>
            </div>
            <Link to="/reports/appts-by-booking-method" className="inline-flex items-center gap-1 text-xs font-medium text-accent-700 transition-colors hover:text-accent-800" data-id="element-322">
              Open report <ArrowRightIcon className="h-3 w-3" data-id="element-323" />
            </Link>
          </div>
          <div className="p-4" data-id="element-324">
            {bookingResult?.grouped && <ReportChart data={bookingResult.grouped} chartType="bar" metricLabel="Appointments" data-id="element-325" />}
          </div>
        </motion.section>

        <motion.section initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.34,
        duration: 0.35,
        ease: 'easeOut'
      }} className="card-float rounded-2xl bg-white" aria-labelledby="chart-status" data-id="element-326">
          <div className="flex items-center justify-between border-b border-navy-50 px-5 py-4" data-id="element-327">
            <div data-id="element-328">
              <h3 id="chart-status" className="text-sm font-semibold text-navy-900" data-id="element-329">
                Appointment Status Breakdown
              </h3>
              <p className="text-xs text-navy-400" data-id="element-330">Last 30 days</p>
            </div>
            <Link to="/reports/appt-status-breakdown" className="inline-flex items-center gap-1 text-xs font-medium text-accent-700 transition-colors hover:text-accent-800" data-id="element-331">
              Open report <ArrowRightIcon className="h-3 w-3" data-id="element-332" />
            </Link>
          </div>
          <div className="p-4" data-id="element-333">
            {statusResult?.grouped && <ReportChart data={statusResult.grouped} chartType="pie" metricLabel="Appointments" data-id="element-334" />}
          </div>
        </motion.section>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3" data-id="element-335">
        {[{
        to: '/reports',
        icon: FileBarChart2Icon,
        title: 'Report Library',
        desc: `${premadeReports.length} pre-built reports across appointments, results, and clearances.`,
        cta: 'Browse reports'
      }, {
        to: '/builder',
        icon: WrenchIcon,
        title: 'Report Builder',
        desc: 'Build a custom report from your datasets with live preview.',
        cta: 'Start building'
      }, {
        to: '/templates',
        icon: BookmarkIcon,
        title: 'My Templates',
        desc: `${templates.length} saved report templates, ready to re-run or refine.`,
        cta: 'View templates'
      }].map((card, i) => <motion.div key={card.to} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.42 + i * 0.06,
        duration: 0.35,
        ease: 'easeOut'
      }} data-id="element-336">
            <Link to={card.to} className="card-float group block h-full rounded-2xl bg-white p-5 transition-shadow hover:card-float-lg" data-id="element-337">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy-900" data-id="element-338">
                <card.icon className="h-4 w-4 text-gold-300" data-id="element-339" />
              </span>
              <p className="mt-3 text-sm font-semibold text-navy-900" data-id="element-340">
                {card.title}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-navy-400" data-id="element-341">
                {card.desc}
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent-700 transition-all group-hover:gap-2" data-id="element-342">
                {card.cta} <ArrowRightIcon className="h-3 w-3" data-id="element-343" />
              </span>
            </Link>
          </motion.div>)}
      </div>
    </div>;
}